import React from 'react';
import './App.css';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import AppShell from './components/layout/AppShell';
import Dashboard from './pages/Dashboard';
import Leads from './pages/Leads';
import LeadProfile from './pages/LeadProfile';
import Outbound from './pages/Outbound';
import OutboundImport from './pages/OutboundImport';
import GrokLeads from './pages/GrokLeads';
import Pipeline from './pages/Pipeline';
import Fundings from './pages/Fundings';
import Lenders from './pages/Lenders';
import Navigate from './pages/Navigate';
import Contacts from './pages/Contacts';
import SearchResults from './pages/SearchResults';
import LoanStatus from './pages/LoanStatus';
import LoanSummary from './pages/LoanSummary';
import Fees from './pages/Fees';
import Documents from './pages/Documents';
import Messages from './pages/Messages';
import PlaceholderPage from './pages/PlaceholderPage';
import Apply from './pages/Apply';
import Login from './pages/Login';
import SetPassword from './pages/SetPassword';
import MailerLayout from './components/mailer/MailerLayout';
import MailerDashboard from './pages/MailerDashboard';
import MailerCampaigns from './pages/MailerCampaigns';
import MailerSpend from './pages/MailerSpend';
import MailerStates from './pages/MailerStates';
import MailerDrilldown from './pages/MailerDrilldown';
import MailerUpload from './pages/MailerUpload';
import MailerDataQuality from './pages/MailerDataQuality';
import AdminTeam from './pages/AdminTeam';
import AdminReports from './pages/AdminReports';
import Settings from './pages/Settings';
import ProtectedRoute from './components/auth/ProtectedRoute';
import { installAxiosAuthInterceptors } from './lib/authStore';
import { Toaster } from './components/ui/sonner';

// Install axios auth interceptors at module load — runs ONCE, before any
// component mounts. Adds Bearer header to every request and force-redirects
// to /login on 401 from a protected endpoint.
installAxiosAuthInterceptors();

// One-time migration: clear localStorage keys that used to mirror business data
// now owned by MongoDB. Idempotent — safe to run on every boot.
(function migrateLocalStorage() {
  try {
    const keys = Object.keys(localStorage);
    keys.forEach((k) => {
      if (k.startsWith('kf_app_')) localStorage.removeItem(k);              // form mirror — now in lead.application
      if (k.startsWith('kf_co_nudge_dismissed_')) localStorage.removeItem(k);  // nudge dismissal — now in lead.nudges_dismissed
    });
  } catch (_) { /* private mode / quota — non-fatal */ }
})();


// Smart home route: on phones (<lg, i.e. <1024px) we land directly on
// Global Search — that's the #1 daily action for LOs in the field.
// On desktop/tablet we keep the analytical Dashboard as the home page.
// Tracks viewport changes so resizing or rotating the device swaps in real time.
function HomeRoute() {
  const [isDesktop, setIsDesktop] = React.useState(
    typeof window !== 'undefined' && window.matchMedia('(min-width: 1024px)').matches
  );
  React.useEffect(() => {
    const mq = window.matchMedia('(min-width: 1024px)');
    const handler = (e) => setIsDesktop(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);
  return isDesktop ? <Dashboard /> : <SearchResults />;
}



function CrmShell() {
  return (
    <AppShell>
      <Routes>
        <Route path="/" element={<HomeRoute />} />
        <Route path="/leads" element={<Leads />} />
        <Route path="/leads/:id" element={<LeadProfile />} />
        <Route path="/outbound" element={<Outbound />} />
        <Route path="/outbound/import" element={<ProtectedRoute adminOnly><OutboundImport /></ProtectedRoute>} />
        <Route path="/grok-leads" element={<GrokLeads />} />
        <Route path="/leads-marketplace" element={<GrokLeads />} />
        <Route path="/search" element={<SearchResults />} />
        <Route path="/pipeline" element={<Pipeline />} />
        <Route path="/fundings" element={<Fundings />} />
        <Route path="/lenders" element={<Lenders />} />
        <Route path="/navigate" element={<Navigate />} />
        <Route path="/navigate/:id" element={<Navigate />} />
        <Route path="/contacts" element={<Contacts />} />
        <Route path="/loans/:id" element={<LoanStatus />} />
        <Route path="/loans/:id/summary" element={<LoanSummary />} />
        <Route path="/loans/:id/fees" element={<Fees />} />
        <Route path="/loans/:id/documents" element={<Documents />} />
        <Route path="/loans/:id/messages" element={<Messages />} />
        <Route path="/loan-summary" element={<LoanSummary />} />
        <Route path="/fees" element={<Fees />} />
        <Route path="/documents" element={<Documents />} />
        <Route path="/messages" element={<Messages />} />
        <Route path="/mailer" element={<ProtectedRoute adminOnly><MailerLayout /></ProtectedRoute>}>
          <Route index element={<MailerDashboard />} />
          <Route path="campaigns" element={<MailerCampaigns />} />
          <Route path="spend" element={<MailerSpend />} />
          <Route path="states" element={<MailerStates />} />
          <Route path="drilldown" element={<MailerDrilldown />} />
          <Route path="upload" element={<MailerUpload />} />
          <Route path="quality" element={<MailerDataQuality />} />
        </Route>
        <Route path="/team" element={<ProtectedRoute adminOnly><AdminTeam /></ProtectedRoute>} />
        <Route path="/reports" element={<ProtectedRoute adminOnly><AdminReports /></ProtectedRoute>} />
        <Route path="/settings" element={<ProtectedRoute adminOnly><Settings /></ProtectedRoute>} />
        <Route path="*" element={<PlaceholderPage />} />
      </Routes>
    </AppShell>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <Routes>
          {/* Public borrower-facing lead capture — no AppShell, no sidebar */}
          <Route path="/apply" element={<Apply />} />
          {/* Login screen — outside the protected shell */}
          <Route path="/login" element={<Login />} />
          {/* First-login password reset — behind auth so we have the LO's
              bearer token, but OUTSIDE the AppShell so the sidebar/topbar
              don't render around a forced-reset screen. */}
          <Route
            path="/set-password"
            element={
              <ProtectedRoute>
                <SetPassword />
              </ProtectedRoute>
            }
          />
          {/* Internal CRM — wrapped in AppShell, protected by JWT auth */}
          <Route
            path="/*"
            element={
              <ProtectedRoute>
                <CrmShell />
              </ProtectedRoute>
            }
          />
        </Routes>
        <Toaster position="top-right" />
      </BrowserRouter>
    </div>
  );
}

export default App;
